import { create } from "zustand";
import { persist } from "zustand/middleware";
import { buildLayerGroups } from "../layers/layerPanelConfig";
import { PERSIST_KEYS } from "../auth/persistKeys";
import { fetchWithAuth } from "../api/fetchWithAuth";

/* -------------------------------------------------
   HELPERS — LAYERS ONLY
------------------------------------------------- */

function buildVisibleMap(groups, prev = {}) {
    const visible = { ...prev };

    const walk = (items) => {
        items.forEach((g) => {
            if (g.layers) {
                g.layers.forEach((l) => {
                    if (visible[l.id] === undefined) {
                        visible[l.id] = !!l.defaultOn;
                    }
                });
            }
            if (g.groups) walk(g.groups);
        });
    };

    walk(groups);
    return visible;
}

function buildOpacityMap(groups, prev = {}) {
    const opacity = { ...prev };

    const walk = (items) => {
        items.forEach((g) => {
            if (g.layers) {
                g.layers.forEach((l) => {
                    if (opacity[l.id] === undefined) {
                        opacity[l.id] = l.opacity ?? 1.0;
                    }
                });
            }
            if (g.groups) walk(g.groups);
        });
    };

    walk(groups);
    return opacity;
}

function buildOrderMap(groups, prev = {}) {
    const order = { ...prev };

    const walk = (items) => {
        items.forEach((g) => {
            if (g.layers) {
                order[g.id] = g.layers.map((l) => l.id);
            }
            if (g.groups) walk(g.groups);
        });
    };

    walk(groups);
    return order;
}

/* -------------------------------------------------
   DEFAULT ASSETS (AUTHORITATIVE)
------------------------------------------------- */

const DEFAULT_ASSETS = Object.freeze({
    wells: {
        enabled: false,
        geometry: {
            point: false,
            line: false,
        },
        filters: [], // empty = show all
    },
    // The two regulatory point layers added alongside the producing-well pair.
    // SEPARATE ASSET KEYS, NOT MORE GEOMETRY FACETS OF `wells`, and that is the
    // whole reason this change touches the store at all. RrcInfoPanel keys a
    // row's on-state and its toggle on (asset, layer.geometry) — see
    // RrcInfoPanel.jsx:100,106 — so a third and fourth `geometry: "point"` row
    // under `wells` would all share assets.wells.geometry.point, toggle
    // together, and be indistinguishable on screen.
    //
    // Point-only by nature: a UIC well and a permitted location are each a
    // single coordinate. Neither has a lateral, so neither gets a `line` facet,
    // and `enabled` stays auto-derived by toggleAssetGeometry exactly as it is
    // for wells and pads.
    //
    // No `filters` key: the status filter on `wells` reads props.status_norm,
    // which the RRC ingest populates for producing wells only. An empty array
    // here would imply a filter UI that does not exist.
    wells_uic: {
        enabled: false,
        geometry: {
            point: false,
        },
    },
    permits: {
        enabled: false,
        geometry: {
            point: false,
        },
    },
    pads: {
        // DEFAULT OFF, like every other feature.
        //
        // These were enabled+polygon ON to match PadLayer's historical
        // always-on behaviour, which made Well Pads the one row in the
        // FEATURES catalog that was lit before the user chose anything --
        // indistinguishable from a layer set having selected it. Nothing
        // should be on until either the user turns it on or a saved layer set
        // restores it.
        //
        // Safe to change: `assets` is not persisted (see partialize/migrate),
        // so this default is read fresh every load rather than being shadowed
        // by a stored blob.
        enabled: false,
        geometry: {
            polygon: false,
        },
        filters: [], // no status filter for pads in v0
    },
});

/* -------------------------------------------------
   SEED — the static half of the group tree, built once.

   Computed at module load rather than inside the store initializer so the
   three id-keyed maps below are derived from the SAME array the store holds,
   not from three separate builds of an equal one.
------------------------------------------------- */
const SEED_GROUPS = buildLayerGroups({ imageryLayers: [], userRole: "viewer" });

/* -------------------------------------------------
   STORE
------------------------------------------------- */

export const useLayersStore = create(
    persist(
        (set, get) => ({
            /* -----------------------------
               CORE STATE
            ----------------------------- */
            //
            // ── SEEDED AT CREATION, NOT LEFT EMPTY FOR rebuildGroups ──────
            //
            // `groups` used to start `[]` and be filled by rebuildGroups, which
            // lives inside MapPage's project-bootstrap effect — and that effect
            // returns at its first line when there is no project or the project
            // is "global" (MapPage.jsx:224). So in global scope NOTHING EVER
            // BUILT THE GROUPS, and RrcInfoPanel:278 (`if (!groups?.length)`)
            // sat on "Loading layers…" indefinitely.
            //
            // THAT WAS TRUE BEFORE `groups` STOPPED BEING PERSISTED, TOO. It
            // was invisible because rehydrate supplied a copy from localStorage,
            // so the panel had content whether or not anything had computed it.
            // Removing `groups` from partialize did not break global scope; it
            // revealed that global scope was never wired and had been running on
            // a cached artifact since the last time a project loaded in that
            // browser.
            //
            // Seeding here is right on the merits rather than merely
            // convenient: LAYER_GROUPS_STATIC is a static import and the RRC
            // section it describes is global reference data — org-independent
            // and project-independent. A section made of a static import has no
            // business waiting on a project fetch. deriveImageryGroups returns
            // [] for an empty array, so this is side-effect free at module init,
            // and rebuildGroups still runs on project load to add the imagery
            // half.
            //
            // THE THREE ID-KEYED MAPS ARE SEEDED FROM THE SAME GROUPS, because
            // rebuildGroups is what used to seed them and it no longer runs
            // first. Two mechanisms keep that from touching a returning user's
            // state, and both are tested: each builder takes `prev` and fills
            // only keys that are `undefined`, so a seed can never overwrite a
            // value; and rehydrate REPLACES these objects wholesale, since
            // zustand merges `{ ...current, ...persisted }` at the top level, so
            // a persisted map wins over the seed rather than being merged with
            // it.
            groups: SEED_GROUPS,
            visibleById: buildVisibleMap(SEED_GROUPS),
            opacityById: buildOpacityMap(SEED_GROUPS),
            boundsById: {},
            orderByContainer: buildOrderMap(SEED_GROUPS),

            /* -----------------------------
               ASSETS — EPHEMERAL (NOT PERSISTED)
            ----------------------------- */
            // Note: `assets` is intentionally excluded from persistence via
            // `partialize` below. Any session-scoped force-on toggling done
            // by product components (WellLayer, etc.) lives only in memory.
            // Long-lived layer state belongs in saved layer-sets.
            assets: structuredClone(DEFAULT_ASSETS),

            /* -----------------------------
               FEATURE CATALOG — derived flat list off vector_assets
               (ephemeral, NOT persisted — see partialize). featureCatalog is
               the org's available feature types from GET /api/v1/feature-catalog;
               activeFeatureTypes is the set toggled on in the FEATURES tab and
               rendered by the generic VectorFeatureLayers component.
            ----------------------------- */
            // [{ key, label, geom_class, origin, category, category_label,
            //    sort_order, grantable }] — the vocabulary travels with the row,
            // so FeatureCatalogList needs no label or ordering constant of its
            // own. Arrives already sorted by sort_order then label.
            featureCatalog: [],
            featureCatalogLoaded: false,
            // AN EMPTY CATALOG AND A FAILED FETCH ARE DIFFERENT THINGS AND MUST
            // NOT RENDER THE SAME. Both used to end as `[]` with loaded: true,
            // which was survivable while the empty message was generic. It is
            // not survivable now: the message says "no feature layers are
            // enabled for your organization", and showing that when the server
            // is unreachable tells a paying customer they bought nothing.
            featureCatalogError: null,
            activeFeatureTypes: {},    // asset_type -> bool

            /* -----------------------------
               INTERNAL FLAGS
            ----------------------------- */
            _initialized: false,
            _hasHydrated: false,

            /* -----------------------------
               INIT (SAFE TO CALL MULTIPLE TIMES)
            ----------------------------- */
            init() {
                const state = get();
                if (state._initialized) return;

                set((s) => ({
                    _initialized: true,
                    assets: s.assets || structuredClone(DEFAULT_ASSETS),
                }), false, "layers/init");
            },

            /* -----------------------------
               GROUP REBUILD (IMAGERY + STATIC)
            ----------------------------- */
            rebuildGroups({ layers = [], userRole = "viewer" }) {
                const groups = buildLayerGroups({ layers, userRole });

                set((state) => ({
                    groups,
                    visibleById: buildVisibleMap(groups, state.visibleById),
                    opacityById: buildOpacityMap(groups, state.opacityById),
                    orderByContainer: buildOrderMap(groups, state.orderByContainer),
                }));
            },

            /* -----------------------------
               LAYER ACTIONS (UNCHANGED)
            ----------------------------- */
            toggleLayer(id) {
                set((state) => ({
                    visibleById: {
                        ...state.visibleById,
                        [id]: !state.visibleById[id],
                    },
                }));
            },

            setLayerBounds(id, bounds) {
                set({
                    boundsById: {
                        ...get().boundsById,
                        [id]: bounds,
                    },
                });
            },

            setOpacity(id, value) {
                set({
                    opacityById: {
                        ...get().opacityById,
                        [id]: value,
                    },
                });
            },

            reorder(containerId, activeId, overId) {
                const groups = structuredClone(get().groups);
                const orderByContainer = { ...get().orderByContainer };

                const reorderIn = (layers) => {
                    const from = layers.findIndex((l) => l.id === activeId);
                    const to = layers.findIndex((l) => l.id === overId);
                    if (from === -1 || to === -1) return;
                    layers.splice(to, 0, layers.splice(from, 1)[0]);
                };

                groups.forEach((g) => {
                    if (g.layers && g.id === containerId) {
                        reorderIn(g.layers);
                        orderByContainer[g.id] = g.layers.map((l) => l.id);
                    }
                    if (g.groups) {
                        g.groups.forEach((sub) => {
                            if (sub.id === containerId) {
                                reorderIn(sub.layers);
                                orderByContainer[sub.id] = sub.layers.map((l) => l.id);
                            }
                        });
                    }
                });

                set({ groups, orderByContainer });
            },

            /* -----------------------------
               ASSET ACTIONS (NEW)
            ----------------------------- */

            toggleAsset(assetId) {
                set((state) => ({
                    assets: {
                        ...state.assets,
                        [assetId]: {
                            ...state.assets[assetId],
                            enabled: !state.assets[assetId]?.enabled,
                        },
                    },
                }));
            },

            toggleAssetGeometry(assetId, geom) {
                set((state) => {
                    const asset = state.assets?.[assetId] ?? {};
                    const currentGeometry = asset.geometry ?? {};
                    const newGeometry = {
                        ...currentGeometry,
                        [geom]: !currentGeometry[geom],
                    };

                    // Auto-derive `enabled` from whether ANY geometry is on. The
                    // master "enabled" flag has no separate UI surface — it's a
                    // computed-but-stored summary used by the MapView visibility
                    // effect. Keeping it in sync with geometry sub-toggles ensures
                    // the OPERATIONAL panel toggles work as users expect.
                    const anyGeometryOn = Object.values(newGeometry).some(Boolean);

                    return {
                        assets: {
                            ...state.assets,
                            [assetId]: {
                                ...asset,
                                geometry: newGeometry,
                                enabled: anyGeometryOn,
                            },
                        },
                    };
                });
            },

            // SET, not toggle. A caller that wants a geometry ON has to read the
            // current value first to decide whether to toggle, and that
            // read-then-flip is precisely the bug this was added to fix:
            // WellLayer did `if (!rrcEnabled) toggleAsset("wells")`, which flips
            // the DERIVED `enabled` flag and leaves geometry alone — so
            // MapView's visibility effect, which computes
            // `enabled && geometry.point`, immediately hid the layer again.
            //
            // Idempotent, so "make sure RRC surface wells are on" and "put them
            // back how I found them" are both one call with no prior read.
            //
            // `enabled` is auto-derived here the same way toggleAssetGeometry
            // derives it. Two places deriving one summary differently is how a
            // master flag and its sub-toggles come apart.
            setAssetGeometry(assetId, geom, on) {
                set((state) => {
                    const asset = state.assets?.[assetId] ?? {};
                    const currentGeometry = asset.geometry ?? {};
                    if (currentGeometry[geom] === !!on) return state;   // no-op, no re-render
                    const newGeometry = { ...currentGeometry, [geom]: !!on };
                    return {
                        assets: {
                            ...state.assets,
                            [assetId]: {
                                ...asset,
                                geometry: newGeometry,
                                enabled: Object.values(newGeometry).some(Boolean),
                            },
                        },
                    };
                });
            },

            /* -----------------------------
               FEATURE CATALOG ACTIONS
            ----------------------------- */
            async loadFeatureCatalog() {
                try {
                    const res = await fetchWithAuth("/api/v1/feature-catalog");
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    const data = await res.json();
                    set({
                        featureCatalog: data.features ?? [],
                        featureCatalogLoaded: true,
                        featureCatalogError: null,
                    });
                } catch (e) {
                    console.warn("[layers] feature catalog load failed", e);
                    set({
                        featureCatalog: [],
                        featureCatalogLoaded: true,
                        featureCatalogError: e?.message ?? String(e),
                    });
                }
            },

            // Toggle a derived feature type on/off. `well_pad` is special: pads
            // are rendered by PadLayer (which carries PadRekko QC interaction),
            // so its catalog toggle routes to the existing assets.pads visibility
            // rather than the generic renderer — no double-render.
            toggleFeatureType(key) {
                if (key === "well_pad") {
                    get().toggleAssetGeometry("pads", "polygon");
                    return;
                }
                set((state) => ({
                    activeFeatureTypes: {
                        ...state.activeFeatureTypes,
                        [key]: !state.activeFeatureTypes[key],
                    },
                }));
            },

            // Replace the whole active-feature-types set in one write. Used by
            // the layer-set model: applySnapshot restores the saved features and
            // the project bootstrap clears them ({}) so a project with no default
            // set doesn't inherit the previous project's features. well_pad is
            // intentionally NOT carried here — it routes through assets.pads,
            // which the snapshot captures/restores separately.
            setActiveFeatureTypes(map) {
                set({ activeFeatureTypes: { ...(map ?? {}) } });
            },

            // Is a derived feature type currently visible? (well_pad mirrors pads.)
            isFeatureTypeOn(key) {
                const s = get();
                if (key === "well_pad") {
                    return !!(s.assets?.pads?.enabled && s.assets?.pads?.geometry?.polygon);
                }
                return !!s.activeFeatureTypes[key];
            },

            toggleAssetFilter(assetId, filterId) {
                set((state) => {
                    const asset = state.assets?.[assetId] ?? {};
                    const raw = asset.filters;
                    const filters = new Set(Array.isArray(raw) ? raw : []);

                    if (filters.has(filterId)) {
                        filters.delete(filterId);
                    } else {
                        filters.add(filterId);
                    }

                    return {
                        assets: {
                            ...state.assets,
                            [assetId]: {
                                ...asset,
                                filters: Array.from(filters),
                            },
                        },
                    };
                });
            },
        }),
        {
            name: PERSIST_KEYS.layerState,
            version: 9,

            // Persist only the durable bits. `assets` is product-driven UI
            // state (e.g. WellLayer flips wells.enabled while WellFinder is
            // open) and must NOT survive across sessions — otherwise a user
            // who closed the tab with WellFinder open comes back next session
            // with RRC layers stuck on, regardless of which user logs in.
            //
            // ── partialize GOVERNS WRITES, NOT READS. THE COMMENT THAT USED TO
            //    SIT HERE SAID OTHERWISE AND IT WAS WRONG. ───────────────────
            //
            // It read: "To make a layer-state field persistent, add it to this
            // object. Anything not listed is dropped from localStorage on every
            // hydrate cycle." The first sentence is true. THE SECOND IS FALSE,
            // and it is false in the direction that makes a removal look
            // sufficient when it is not.
            //
            // Demonstrated rather than reasoned: seed localStorage with a v8
            // blob carrying `assets: { SENTINEL_MARKER: … }` — a key removed
            // from partialize at v8 — and after rehydrate the store's `assets`
            // is `{ SENTINEL_MARKER: … }`. Not merged alongside DEFAULT_ASSETS:
            // REPLACING it. zustand's default merge is
            // `{ ...currentState, ...persistedState }`, so any key present in
            // the stored blob lands in state whether or not partialize would
            // write it back. The blob is only rewritten WITHOUT the key after a
            // subsequent set(), which is one load too late.
            //
            // What actually protects `assets` is the `delete` in migrate below,
            // not its absence here. Removing a field from this object stops it
            // being written; only migrate stops it being read.
            partialize: (state) => ({
                // `groups` IS NOT HERE, AND MUST NOT BE. It is derived —
                // buildLayerGroups() = deriveImageryGroups(...) +
                // LAYER_GROUPS_STATIC — so persisting it means a stale copy in
                // a browser silently overrides the config file in the source
                // tree. That is not hypothetical: it shipped. 3b6193a4 added
                // two nested sub-groups to layerPanelConfigStatic.js and the
                // panel kept rendering the old two rows, in a freshly built
                // bundle that provably contained the new config, because
                // rehydrate replaced `groups` with the pre-change array.
                //
                // The three id-keyed maps below stay: they are genuine user
                // state — what the user toggled, faded and reordered — and
                // survive a config change correctly. They do accumulate entries
                // for ids that no longer exist, which is a smaller version of
                // the same problem and is not addressed here.
                visibleById: state.visibleById,
                opacityById: state.opacityById,
                boundsById: state.boundsById,
                orderByContainer: state.orderByContainer,
            }),

            migrate(persistedState, fromVersion) {
                // v6 → v7 originally forced wells off in persisted state.
                //
                // v7 → v8 dropped `assets`. The comment here used to say it was
                // "simply ignored on rehydrate" because partialize excluded it.
                // That was wrong — see the note above partialize — and this
                // delete is the only reason it was ever ignored.
                if (fromVersion < 8 && persistedState?.assets) {
                    delete persistedState.assets;
                }

                // v8 → v9 drops `groups`, which is derived and should never
                // have been persisted. THIS IS THE HALF THAT REPAIRS EXISTING
                // BROWSERS. Removing `groups` from partialize stops it being
                // written from now on; every browser that has already loaded
                // the app is carrying a snapshot that still has it, and without
                // this delete that snapshot would keep overriding the config
                // file on the next load, and the one after.
                if (fromVersion < 9 && persistedState?.groups) {
                    delete persistedState.groups;
                }

                return persistedState;
            },

            onRehydrateStorage: () => (state, error) => {
                try {
                    if (error) {
                        state?.init?.();
                        return;
                    }

                    if (state && typeof state === "object") {
                        state._hasHydrated = true;
                    }

                    // After rehydrate, `state.assets` is the in-memory
                    // default (DEFAULT_ASSETS) because partialize excluded
                    // it. init() is a no-op for assets but still useful for
                    // the _initialized flag.
                    state?.init?.();
                } catch (e) {
                    console.warn("[layers] rehydrate/init failed", e);
                }
            }
        }
    )
);
